// =============================================================================
// components/R2ServiceDiscoveryModal.tsx
// Customer Engagement Gate — 3-step value-first experience
// Step 1: Discovery (project type + context)
// Step 2: Value Preview (deliverables + construction animation + AI analysis)
// Step 3: Free package request (commercial pricing stays outside discovery)
//
// Design principle: "Never make customers ask: why is this expensive?"
//                   "Make them think: I understand exactly what I am getting."
// =============================================================================
import { useState, useEffect, useRef, useCallback, type JSX } from 'react';
import {
  X, ChevronRight, CheckCircle2, Clock, Upload, FileText,
  Zap, Star, Shield, ArrowRight, Bot, Sparkles, Building2,
  Lock, Eye, Package, Loader2,
} from 'lucide-react';
import { hasFounderToken, useFounderMode } from '../stores/founderMode.store.js';
import { useAuthStore } from '../stores/auth.store.js';
import { isMasterAdmin } from '@bim/shared/config/masterKey.config.js';

const API = (import.meta as any).env?.VITE_BACKEND_URL
  ?? (typeof process !== 'undefined' ? (process.env as any)?.REACT_APP_BACKEND_URL ?? '' : '');

const RETURNING_KEY = 'r2_mp_completed_services';

const C = {
  bg:    '#010812',
  panel: 'rgba(4,12,28,0.98)',
  card:  'rgba(6,15,35,0.94)',
  cyan:  '#00d4ff',
  orange:'#ff6b35',
  green: '#4ade80',
  amber: '#fbbf24',
  text:  '#f0f6ff',
  muted: 'rgba(240,246,255,0.55)',
  dim:   'rgba(0,212,255,0.07)',
  border:'rgba(0,212,255,0.15)',
  mono:  "'JetBrains Mono','Space Mono',monospace",
};

// ─── Discovery question banks per service category ────────────────────────────
const DISCOVERY_QUESTIONS: Record<string, { question: string; subtext?: string; options: { id: string; label: string; icon?: string; detail?: string }[] }[]> = {
  'Floor Plans': [
    {
      question: 'What type of project are you working on?',
      subtext: 'R2 will tailor the deliverable package to your specific scenario.',
      options: [
        { id: 'new_build',    label: 'New Construction',         detail: 'Ground-up build from design drawings' },
        { id: 'renovation',   label: 'Renovation / Upgrade',     detail: 'Modifications to existing facility' },
        { id: 'asbuilt',      label: 'As-Built Documentation',   detail: 'Capture existing conditions' },
        { id: 'permit',       label: 'Permit Submission',        detail: 'Authority-ready permit package' },
        { id: 'tenant',       label: 'Tenant Fit-Out',           detail: 'Interior build-out within existing shell' },
        { id: 'other',        label: 'Other',                    detail: 'Tell us more' },
      ],
    },
    {
      question: 'Do you have existing drawings to upload?',
      subtext: 'Upload improves accuracy. You can also proceed without drawings and describe your project.',
      options: [
        { id: 'upload_now',   label: 'Yes — I will upload now',      detail: 'PDF, CAD, Revit, photos' },
        { id: 'upload_later', label: 'Yes — I will upload after ordering', detail: 'Upload link provided with order confirmation' },
        { id: 'no_drawings',  label: 'No drawings — start from description', detail: 'R2 works from your written scope' },
        { id: 'ai_generate',  label: 'Generate new from scratch',    detail: 'R2 creates floor plan concept from brief' },
      ],
    },
  ],
  'BIM & Digital Twin': [
    {
      question: 'What is your primary goal?',
      subtext: 'This helps R2 scope the right level of detail for your deliverable.',
      options: [
        { id: 'digital_twin',   label: 'Full Digital Twin',         detail: 'Live data, operations, lifecycle tracking' },
        { id: 'bim_model',      label: 'BIM Model (LOD 300–400)',   detail: 'Construction-ready 3D model' },
        { id: 'revit_migration',label: 'AutoCAD → Revit Migration', detail: 'Convert legacy drawings to BIM' },
        { id: 'pdf_to_cad',     label: 'PDF → CAD Entities',        detail: 'Convert PDF sheets into editable CAD lines, blocks, text, and layers' },
        { id: 'validation',     label: 'Design Validation',         detail: 'AI review against standards' },
        { id: 'clash_detect',   label: 'Clash Detection',           detail: 'Multi-discipline coordination' },
      ],
    },
    {
      question: 'What discipline(s) are in scope?',
      options: [
        { id: 'arch_struct', label: 'Architecture + Structure',  detail: 'Building envelope, cores, slabs' },
        { id: 'mep',         label: 'MEP Systems',               detail: 'Mechanical, electrical, plumbing' },
        { id: 'telecom',     label: 'ICT + Telecom',             detail: 'Structured cabling, networks, AV' },
        { id: 'all_disc',    label: 'All Disciplines',           detail: 'Full federated model' },
        { id: 'civil',       label: 'Civil + Site',              detail: 'Site works, drainage, roads' },
      ],
    },
  ],
  'Data Centre': [
    {
      question: 'What type of data centre are you building?',
      subtext: 'Each facility type has distinct design requirements. R2 adapts the scope accordingly.',
      options: [
        { id: 'enterprise_dc',  label: 'Enterprise Data Centre',   detail: 'Corporate private DC (100–500 racks)' },
        { id: 'hyperscale',     label: 'AI / Hyperscale Facility', detail: 'GPU clusters, high-density (500+ racks)' },
        { id: 'colo',           label: 'Colocation Facility',      detail: 'Multi-tenant caged environment' },
        { id: 'edge',           label: 'Edge Data Centre',         detail: 'Distributed micro-DC (< 50 racks)' },
        { id: 'upgrade',        label: 'Existing Facility Upgrade',detail: 'Retrofit or capacity expansion' },
      ],
    },
    {
      question: 'What is the approximate scale of this project?',
      options: [
        { id: 'micro',   label: 'Micro (< 50 racks)',    detail: 'Edge, branch, or small enterprise' },
        { id: 'small',   label: 'Small (50–200 racks)',  detail: 'SME or regional data centre' },
        { id: 'medium',  label: 'Medium (200–500 racks)',detail: 'Regional or mid-tier enterprise' },
        { id: 'large',   label: 'Large (500–2,000 racks)',detail: 'Carrier-class or large enterprise' },
        { id: 'hyper',   label: 'Hyperscale (2,000+)',   detail: 'Cloud, AI training, or hyperscale provider' },
      ],
    },
  ],
  'Healthcare': [
    {
      question: 'What type of healthcare facility are you designing?',
      subtext: 'Clinical technology systems vary significantly by facility type and acuity level.',
      options: [
        { id: 'hospital',      label: 'Hospital (Acute Care)',     detail: 'Full-service inpatient facility' },
        { id: 'outpatient',    label: 'Outpatient / Clinic',       detail: 'Ambulatory care and specialist services' },
        { id: 'imaging',       label: 'Imaging Centre',            detail: 'MRI, CT, X-ray, PET-CT environments' },
        { id: 'icu',           label: 'ICU / Critical Care',       detail: 'Intensive care unit design' },
        { id: 'or_suite',      label: 'Surgical / OR Suite',       detail: 'Operating rooms and perioperative' },
        { id: 'ltc',           label: 'Long-Term Care',            detail: 'Aged care or rehabilitation facility' },
      ],
    },
    {
      question: 'What phase is this project in?',
      options: [
        { id: 'new_facility',    label: 'New Facility',           detail: 'Ground-up construction' },
        { id: 'major_reno',      label: 'Major Renovation',       detail: 'Significant clinical redesign' },
        { id: 'tech_refresh',    label: 'Technology Refresh',     detail: 'Updating systems in existing facility' },
        { id: 'wing_addition',   label: 'Wing Addition',          detail: 'New patient care wing or floor' },
      ],
    },
  ],
  'OSP & Telecom': [
    {
      question: 'What type of infrastructure are you designing?',
      options: [
        { id: 'campus_fiber',   label: 'Campus Fiber Network',      detail: 'Multi-building enterprise campus' },
        { id: 'urban_osp',      label: 'Urban OSP Route',           detail: 'City or street-level fiber routing' },
        { id: 'building_entry', label: 'Building Entry / MPOE',     detail: 'Service entrance and MDF design' },
        { id: 'distribution',   label: 'Distribution Network',      detail: 'Last-mile or access network' },
        { id: 'riser',          label: 'In-Building Riser',         detail: 'Vertical telecom backbone' },
        { id: 'wireless',       label: 'Wireless Infrastructure',   detail: 'Wi-Fi, DAS, CBRS, small cells' },
      ],
    },
    {
      question: 'What standards apply to this project?',
      options: [
        { id: 'bicsi',       label: 'BICSI / TDMM',           detail: 'Telecommunications Distribution Methods Manual' },
        { id: 'ansi_tia',    label: 'ANSI/TIA-568/942',       detail: 'Commercial building + data centre standards' },
        { id: 'iso_11801',   label: 'ISO/IEC 11801',          detail: 'International structured cabling standard' },
        { id: 'local_auth',  label: 'Local Authority Having Jurisdiction', detail: 'AHJ-specific code compliance' },
        { id: 'unknown',     label: 'Not Sure — Advise Me',   detail: 'R2 will recommend applicable standards' },
      ],
    },
  ],
  'Security': [
    {
      question: 'What type of facility are you securing?',
      options: [
        { id: 'corporate',    label: 'Corporate Office',              detail: 'Commercial office or headquarters' },
        { id: 'healthcare_s', label: 'Healthcare Facility',           detail: 'Hospital, clinic, or medical campus' },
        { id: 'education',    label: 'Education / Campus',            detail: 'School, university, or research facility' },
        { id: 'retail',       label: 'Retail / Commercial',           detail: 'Retail centre or mixed-use development' },
        { id: 'government',   label: 'Government / Critical Infra',   detail: 'Restricted access or sensitive facilities' },
        { id: 'industrial_s', label: 'Industrial / Warehouse',        detail: 'Manufacturing or logistics facilities' },
      ],
    },
    {
      question: 'Which systems are in scope?',
      options: [
        { id: 'cctv_only',   label: 'CCTV / Video Surveillance',    detail: 'Camera layout and recording infrastructure' },
        { id: 'acs_only',    label: 'Access Control',               detail: 'Door hardware, credentials, controllers' },
        { id: 'intrusion',   label: 'Intrusion Detection',          detail: 'Alarm zones, motion, perimeter' },
        { id: 'integrated',  label: 'Integrated Security Package',  detail: 'All systems coordinated under one design' },
      ],
    },
  ],
  'Infrastructure': [
    {
      question: 'What electrical system are you designing?',
      options: [
        { id: 'sld',          label: 'Single-Line Diagram',         detail: 'Power distribution hierarchy' },
        { id: 'emergency_pwr',label: 'Emergency Power',             detail: 'Generator, UPS, transfer switches' },
        { id: 'power_quality',label: 'Power Quality & Grounding',   detail: 'Harmonic analysis, isolated ground' },
        { id: 'lighting',     label: 'Lighting Design',             detail: 'Luminaire selection and layout' },
        { id: 'arc_flash',    label: 'Arc Flash Study',             detail: 'NFPA 70E compliance analysis' },
      ],
    },
  ],
};

// Default questions for categories without specific ones
const DEFAULT_QUESTIONS = [
  {
    question: 'What is the primary objective for this project?',
    subtext: 'Help R2 tailor the deliverable package to your specific requirements.',
    options: [
      { id: 'new_install',    label: 'New Installation',           detail: 'First-time deployment' },
      { id: 'upgrade',        label: 'Upgrade Existing',           detail: 'Enhance current systems' },
      { id: 'documentation',  label: 'As-Built / Documentation',   detail: 'Record existing conditions' },
      { id: 'compliance',     label: 'Compliance / Code Review',   detail: 'Standards and regulatory compliance' },
      { id: 'planning',       label: 'Future Planning',            detail: 'Master plan or feasibility study' },
    ],
  },
];

// ─── Deliverables per service (extends animation engine config) ───────────────
const SERVICE_DELIVERABLES: Record<string, string[]> = {
  'floor-plan-gen':    ['Intelligent Floor Plan (PDF + DWG)', 'Room Classification Schedule', 'Area Calculations', 'System Overlay Drawing', 'Documentation Package'],
  'digital-twin':      ['3D BIM Model (RVT + IFC)', 'Asset Information Model', 'MEP Data Layers', 'Lifecycle Tracking Dashboard', 'Operations Manual'],
  'dc-design':         ['Rack Layout Drawing (AutoCAD)', 'Power Infrastructure Plan', 'Fiber Design Package', 'Cooling Zone Layout', 'Equipment Schedule', 'Commissioning Checklist'],
  'hospital-design':   ['Clinical ICT Master Plan', 'Nurse Call Design Drawings', 'PACS Network Architecture', 'RTLS Infrastructure Plan', 'Structured Cabling Design', 'Compliance Documentation'],
  'osp-design':        ['OSP Route Drawing (GIS-enabled)', 'Manhole & Handhole Schedule', 'Fiber Splice Plan', 'Permit Package', 'Bill of Materials', 'OTDR Test Specifications'],
  'riser-diagram':     ['Vertical Backbone Diagram', 'IDF/MDF Detail Drawings', 'Cable Schedule', 'Pathway Routing Plan', 'Standards Compliance Notes'],
  'security-design':   ['CCTV Layout Drawing', 'Access Control Design', 'Intrusion Detection Plan', 'Security Equipment Schedule', 'Compliance Documentation'],
  'sld':               ['Single-Line Diagram', 'Panel Schedules', 'Load Calculations', 'Protection Coordination Study', 'Arc Flash Documentation'],
  'ict-rack-elevation':['Scalable telecom package index', 'T1.x multi-floor floor plans', 'T2.x TR/MDF/IDF layouts', 'T3.x backbone risers', 'T4.x rack elevations', 'T5.x cable + termination schedules'],
  'rack-elevation':    ['Scalable telecom package index', 'T1.x multi-floor floor plans', 'T2.x TR/MDF/IDF layouts', 'T3.x backbone risers', 'T4.x rack elevations', 'T5.x cable + termination schedules'],
  '_default':          ['Scalable engineering drawings (PDF + DWG)', 'Technical documentation', 'Equipment schedule', 'Compliance package', 'Delivery report'],
};

// ─── Analytics tracking ────────────────────────────────────────────────────────
async function trackFunnel(event: string, serviceId: string, meta?: Record<string, unknown>) {
  try {
    await fetch(`${API}/api/r2-marketplace/analytics/funnel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, service_id: serviceId, meta, ts: new Date().toISOString() }),
    });
  } catch { /* analytics are non-blocking */ }
}

// ─── Returning user detection ─────────────────────────────────────────────────
function isReturningUser(serviceId: string): boolean {
  try {
    const data = JSON.parse(localStorage.getItem(RETURNING_KEY) || '{}');
    return !!data[serviceId];
  } catch { return false; }
}

function markServiceCompleted(serviceId: string, answers: Record<string, string>) {
  try {
    const data = JSON.parse(localStorage.getItem(RETURNING_KEY) || '{}');
    data[serviceId] = { completedAt: new Date().toISOString(), answers };
    localStorage.setItem(RETURNING_KEY, JSON.stringify(data));
  } catch { /* returning-user cache is optional */ }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StepIndicator({ step }: { step: 1 | 2 | 3 }): JSX.Element {
  const steps = [
    { n: 1, label: 'Service Discovery' },
    { n: 2, label: 'Value Preview' },
    { n: 3, label: 'Package Options' },
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 28 }}>
      {steps.map((s, i) => {
        const isActive = s.n === step;
        const isDone   = s.n < step;
        return (
          <div key={s.n} style={{ display: 'flex', alignItems: 'center', flex: i < steps.length - 1 ? 1 : 'unset' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 30, height: 30, borderRadius: '50%', display: 'grid', placeItems: 'center', flexShrink: 0,
                background: isDone ? C.cyan : isActive ? 'rgba(0,212,255,0.15)' : 'rgba(240,246,255,0.04)',
                border: `1.5px solid ${isDone ? C.cyan : isActive ? C.cyan : 'rgba(240,246,255,0.12)'}`,
                fontSize: 11, fontWeight: 800, color: isDone ? C.bg : isActive ? C.cyan : 'rgba(240,246,255,0.3)',
                fontFamily: C.mono,
              }}>
                {isDone ? <CheckCircle2 size={14}/> : s.n}
              </div>
              <span style={{ fontSize: 11, fontWeight: isActive ? 700 : 400, color: isActive ? C.text : 'rgba(240,246,255,0.35)' }}>
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 1, background: isDone ? `${C.cyan}40` : 'rgba(240,246,255,0.06)', margin: '0 12px' }}/>
            )}
          </div>
        );
      })}
    </div>
  );
}

function OptionTile({ opt, selected, onSelect }: { opt: { id: string; label: string; detail?: string }; selected: boolean; onSelect: () => void }): JSX.Element {
  return (
    <button onClick={onSelect}
      data-testid={`discovery-option-${opt.id}`}
      style={{
        background: selected ? 'rgba(0,212,255,0.1)' : 'rgba(240,246,255,0.02)',
        border: `1.5px solid ${selected ? C.cyan : 'rgba(240,246,255,0.1)'}`,
        borderRadius: 12, padding: '13px 16px', cursor: 'pointer', textAlign: 'left', width: '100%',
        transition: 'all 0.15s', boxShadow: selected ? `0 0 20px rgba(0,212,255,0.08)` : 'none',
      }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ width: 18, height: 18, borderRadius: '50%', border: `1.5px solid ${selected ? C.cyan : 'rgba(240,246,255,0.2)'}`, background: selected ? C.cyan : 'transparent', display: 'grid', placeItems: 'center', flexShrink: 0, marginTop: 2 }}>
          {selected && <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.bg }}/>}
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: selected ? C.text : 'rgba(240,246,255,0.75)', marginBottom: 3 }}>{opt.label}</div>
          {opt.detail && <div style={{ fontSize: 11, color: 'rgba(240,246,255,0.4)', lineHeight: 1.4 }}>{opt.detail}</div>}
        </div>
      </div>
    </button>
  );
}

const BID_DETAIL_ITEMS = [
  'Issued-for-bid sheet index with title block, revision table, key plan, legends, general notes, and drawing control metadata — sheet count scales by building, floor, area, room, rack, riser zone, and scope complexity',
  'Telecom package minimum categories: T0.0 Cover/Notes, T1.x multi-floor floor plans, T2.x MDF/IDF/TR layouts, T3.x backbone risers, T4.x rack elevations, T5.x cable/termination schedules, with T6.x details when required',
  'Discipline-specific plans, elevations, risers, schedules, details, symbols, mounting notes, pathway notes, and coordination callouts repeated per level/area/room/rack where needed',
  'Editable CAD/BIM intent: layers, blocks, text, linework, sheet references, equipment tags, room IDs, and PDF-to-CAD entity conversion scope',
  'Procurement-ready schedules: equipment list, cable/pathway schedule, rack or device schedule, quantities, alternates, and assumptions register',
  'QA/QC review package: standards checklist, coordination risks, missing inputs, RFIs, limitations, exclusions, and roadmap items before final issue',
  'Client-ready handoff: PDF package, CAD/DXF-ready output where applicable, delivery log, revision history, and secure portal readiness',
];

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN MODAL
// ═══════════════════════════════════════════════════════════════════════════════

export interface ServiceGateProps {
  service: {
    id: string;
    name: string;
    category: string;
    description: string;
    basePrice: number;
    deliveryHours: number;
    delivers?: string[];
  };
  onClose: () => void;
  onProceedToCheckout: (answers: Record<string, string>) => void;
}

export function R2ServiceDiscoveryModal({ service, onClose, onProceedToCheckout }: ServiceGateProps): JSX.Element {
  const isReturning = isReturningUser(service.id);
  const userEmail = useAuthStore(s => s.user?.email);
  const founderActive = useFounderMode(s => s.active);
  const founderMode = isMasterAdmin(userEmail) || founderActive || hasFounderToken();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [currentQ, setCurrentQ] = useState(0);
  const [deliverables, setDeliverables] = useState<string[]>([]);
  const [revealed, setRevealed] = useState<number>(-1); // for staggered reveal
  const [aiSummary, setAiSummary] = useState('');
  const [loadingAI, setLoadingAI] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<string>('');
  const [uploadedFileCount, setUploadedFileCount] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null!);

  const questions = DISCOVERY_QUESTIONS[service.category] ?? DEFAULT_QUESTIONS;
  const currentQuestion = questions[currentQ];
  const selectedAnswer = answers[`q${currentQ}`];

  // On step 2 entry: build deliverables + staggered reveal + AI summary
  useEffect(() => {
    if (step !== 2) return;
    const delivers = SERVICE_DELIVERABLES[service.id] ?? SERVICE_DELIVERABLES['_default'] ?? [];
    setDeliverables(delivers);
    setRevealed(-1);

    // Staggered reveal
    delivers.forEach((_, i) => {
      setTimeout(() => setRevealed(prev => Math.max(prev, i)), 300 + i * 280);
    });

    // Generate AI summary of what R2 will produce
    const projectType = Object.values(answers).join(', ');
    const summary = `Based on your ${service.name.toLowerCase()} requirements for ${projectType || 'your project'}, R2 will produce a comprehensive professional package reviewed by licensed engineers. Delivery in ${service.deliveryHours} hours.`;
    setAiSummary(summary);

    void trackFunnel('preview_viewed', service.id, { answers });
  }, [step]);

  const handleOptionSelect = useCallback((optId: string) => {
    setAnswers(prev => ({ ...prev, [`q${currentQ}`]: optId }));
  }, [currentQ]);

  const handleNextQuestion = useCallback(() => {
    if (currentQ < questions.length - 1) {
      setCurrentQ(q => q + 1);
    } else {
      void trackFunnel('discovery_completed', service.id, { answers });
      setStep(2);
    }
  }, [currentQ, questions.length, answers, service.id]);

  const handleRevealPrice = useCallback(() => {
    void trackFunnel('free_package_viewed', service.id);
    markServiceCompleted(service.id, answers);
    setStep(3);
  }, [service.id, answers]);

  const handleCheckout = useCallback(() => {
    void trackFunnel('checkout_started', service.id);
    onProceedToCheckout(answers);
  }, [service.id, answers, onProceedToCheckout]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length) {
      const allowed = files.filter((file) => /\.(pdf|dwg|dxf|rvt|ifc|ifczip|jpg|jpeg|png|webp|heic|heif|xlsx|xls|csv|docx|txt)$/i.test(file.name));
      const firstPath = (allowed[0] as File & { webkitRelativePath?: string } | undefined)?.webkitRelativePath || allowed[0]?.name || files[0]?.name || 'Project folder';
      const folderName = firstPath.includes('/') ? firstPath.split('/')[0] : 'Selected project files';
      const label = `${folderName} · ${allowed.length || files.length} files`;
      setUploadedFile(label);
      setUploadedFileCount(allowed.length || files.length);
      setAnswers(prev => ({
        ...prev,
        uploaded_file: label,
        uploaded_file_count: String(allowed.length || files.length),
        upload_mode: 'single_recursive_project_folder',
      }));
    }
    e.target.value = '';
  };

  const scopeItems = [
    { label: 'Delivery', value: `${service.deliveryHours} hours` },
    { label: 'Deliverable format', value: 'PDF + DWG + BIM' },
    { label: 'Engineer review', value: 'Included' },
    { label: 'Revisions', value: '1 revision included' },
  ];
  const ctaLabel = founderMode ? 'Activate with Founder Access' : 'Request Free Access';

  return (
    <div data-testid="discovery-modal" data-discovery-step={step}
      style={{ position: 'fixed', inset: 0, background: 'rgba(1,8,18,0.97)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>

      <div style={{ width: '100%', maxWidth: 680, background: '#010e22', border: `1px solid rgba(0,212,255,0.2)`, borderRadius: 20, overflow: 'hidden', boxShadow: '0 0 100px rgba(0,212,255,0.06)' }}>

        {/* ── Header ───────────────────────────────────────────────────────── */}
        <div style={{ padding: '16px 22px', borderBottom: '1px solid rgba(0,212,255,0.1)', display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(0,0,0,0.4)' }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.25)', display: 'grid', placeItems: 'center' }}>
            <Zap size={15} style={{ color: C.cyan }}/>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{service.name}</div>
            <div style={{ fontFamily: C.mono, fontSize: 9, color: 'rgba(0,212,255,0.5)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{service.category}</div>
          </div>
          {isReturning && step === 2 && (
            <span style={{ fontSize: 10, fontFamily: C.mono, padding: '3px 10px', borderRadius: 20, background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.25)', color: C.green }}>Returning Client</span>
          )}
          <button data-testid="discovery-close-btn" onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(240,246,255,0.35)', display: 'flex' }}><X size={16}/></button>
        </div>

        {/* ── Body ─────────────────────────────────────────────────────────── */}
        <div style={{ padding: '24px 24px 20px' }}>

          {!isReturning && <StepIndicator step={step}/>}

          {/* ══════════════════════════════════════════════════════════════════
              STEP 1 — DISCOVERY
          ══════════════════════════════════════════════════════════════════ */}
          {step === 1 && currentQuestion && (
            <div data-testid="discovery-step-1">
              {/* Question progress */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ fontFamily: C.mono, fontSize: 9, color: 'rgba(0,212,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  Question {currentQ + 1} of {questions.length}
                </div>
                <div style={{ height: 2, flex: 1, margin: '0 14px', background: 'rgba(240,246,255,0.06)', borderRadius: 1, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${((currentQ) / questions.length) * 100}%`, background: C.cyan, transition: 'width 0.4s ease', borderRadius: 1 }}/>
                </div>
              </div>

              <h2 style={{ fontSize: 20, fontWeight: 800, color: C.text, margin: '0 0 6px', lineHeight: 1.2 }}>{currentQuestion.question}</h2>
              {currentQuestion.subtext && <p style={{ fontSize: 12, color: C.muted, margin: '0 0 18px', lineHeight: 1.6 }}>{currentQuestion.subtext}</p>}

              {/* Options */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8, marginBottom: 20 }}>
                {currentQuestion.options.map(opt => (
                  <OptionTile key={opt.id} opt={opt}
                    selected={selectedAnswer === opt.id}
                    onSelect={() => handleOptionSelect(opt.id)}
                  />
                ))}
              </div>

              {/* File upload option */}
              {currentQ === 0 && (
                <div style={{ borderTop: '1px solid rgba(240,246,255,0.06)', paddingTop: 14, marginBottom: 16 }}>
                  <div style={{ fontFamily: C.mono, fontSize: 9, color: 'rgba(240,246,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
                    Or upload your project files directly
                  </div>
                  <button onClick={() => fileRef.current?.click()}
                    data-testid="discovery-upload-btn"
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px', background: 'rgba(240,246,255,0.03)', border: '1px dashed rgba(240,246,255,0.15)', borderRadius: 10, cursor: 'pointer', color: C.muted, fontSize: 12 }}>
                    <Upload size={13} style={{ color: C.cyan }}/> Select Project Folder / Drawing Set
                    {uploadedFile && <span data-testid="discovery-upload-summary" style={{ marginLeft: 6, color: C.green, fontWeight: 700 }}>✓ {uploadedFile}</span>}
                  </button>
                  <input
                    ref={fileRef}
                    data-testid="discovery-folder-input"
                    type="file"
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                    accept=".pdf,.dwg,.dxf,.rvt,.ifc,.ifczip,.jpg,.jpeg,.png,.webp,.heic,.heif,.xlsx,.xls,.csv,.docx,.txt"
                    multiple
                    {...({ webkitdirectory: 'true', directory: 'true' } as React.InputHTMLAttributes<HTMLInputElement> & { webkitdirectory: string; directory: string })}
                  />
                  <div data-testid="discovery-folder-upload-note" style={{ fontSize: 10, color: 'rgba(240,246,255,0.35)', marginTop: 7, lineHeight: 1.45 }}>
                    Select the top-level project folder once. R2 ingests all subfolders recursively, so you do not need to keep opening each folder until the files run out.
                    {uploadedFileCount > 0 && <span style={{ color: C.cyan }}> {uploadedFileCount} files queued.</span>}
                  </div>
                </div>
              )}

              <button data-testid="discovery-next-btn"
                onClick={handleNextQuestion}
                disabled={!selectedAnswer && !uploadedFile}
                style={{
                  padding: '11px 28px', borderRadius: 10, border: 'none',
                  background: (selectedAnswer || uploadedFile) ? C.cyan : 'rgba(0,212,255,0.08)',
                  color: (selectedAnswer || uploadedFile) ? C.bg : 'rgba(0,212,255,0.3)',
                  fontWeight: 800, fontSize: 13, cursor: (selectedAnswer || uploadedFile) ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', gap: 7,
                }}>
                {currentQ < questions.length - 1 ? 'Next Question' : 'See What You Receive'} <ChevronRight size={15}/>
              </button>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              STEP 2 — VALUE PREVIEW
          ══════════════════════════════════════════════════════════════════ */}
          {step === 2 && (
            <div data-testid="discovery-step-2">
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontFamily: C.mono, fontSize: 10, color: C.cyan, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
                  Your {service.name} Package includes:
                </div>
                <h2 style={{ fontSize: 22, fontWeight: 900, color: C.text, margin: 0, lineHeight: 1.15 }}>
                  Here is exactly what R2 will deliver.
                </h2>
              </div>

              {/* Deliverables — staggered reveal */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8, marginBottom: 18 }}>
                {deliverables.map((item, i) => (
                  <div key={item}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px',
                      background: 'rgba(0,212,255,0.04)', border: `1px solid rgba(0,212,255,${revealed >= i ? '0.25' : '0.05'})`,
                      borderRadius: 10, transition: 'all 0.4s ease',
                      opacity: revealed >= i ? 1 : 0,
                      transform: revealed >= i ? 'translateX(0)' : 'translateX(-12px)',
                    }}>
                    <CheckCircle2 size={14} style={{ color: C.green, flexShrink: 0 }}/>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: C.text }}>{item}</span>
                  </div>
                ))}
              </div>

              {/* AI-generated summary */}
              {aiSummary && (
                <div style={{ background: 'rgba(0,212,255,0.03)', border: '1px solid rgba(0,212,255,0.12)', borderRadius: 10, padding: '12px 14px', marginBottom: 18, display: 'flex', gap: 10 }}>
                  <Bot size={14} style={{ color: C.cyan, flexShrink: 0, marginTop: 2 }}/>
                  <p style={{ fontSize: 12, color: C.muted, margin: 0, lineHeight: 1.65 }}>{aiSummary}</p>
                </div>
              )}

              <div data-testid="discovery-example-output-preview" style={{ marginBottom: 18, display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: 10 }} className="r2-discovery-output-grid">
                <div style={{ border: '1px solid rgba(0,212,255,0.16)', borderRadius: 12, background: 'rgba(0,0,0,0.22)', padding: 12 }}>
                  <div style={{ fontFamily: C.mono, fontSize: 9, color: C.cyan, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 9 }}>Example output</div>
                  <div style={{ border: '1px solid rgba(0,212,255,0.22)', borderRadius: 8, overflow: 'hidden', background: '#07101f' }}>
                    <div style={{ height: 26, borderBottom: '1px solid rgba(0,212,255,0.16)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 10px', color: C.cyan, fontFamily: C.mono, fontSize: 8.5 }}><span>R2 PACKAGE SHEET</span><FileText size={11}/></div>
                    <div style={{ padding: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div style={{ aspectRatio: '1.35/1', border: '1px solid rgba(251,191,36,0.28)', borderRadius: 6, background: 'linear-gradient(135deg, rgba(251,191,36,0.08), rgba(0,212,255,0.06))', position: 'relative', overflow: 'hidden' }}>
                        {Array.from({ length: 5 }).map((_, i) => <span key={i} style={{ position: 'absolute', left: 10, right: 10, top: 12 + i * 13, height: 2, background: i % 2 ? 'rgba(0,212,255,0.55)' : 'rgba(240,246,255,0.18)' }}/>) }
                      </div>
                      <div style={{ display: 'grid', gap: 6 }}>
                        {['Drawing index', 'Title block', 'Engineer notes', 'BOM / schedule'].map(item => <div key={item} style={{ height: 15, borderRadius: 4, background: 'rgba(240,246,255,0.055)', color: C.muted, fontSize: 9, display: 'flex', alignItems: 'center', padding: '0 7px' }}>{item}</div>)}
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ border: '1px solid rgba(74,222,128,0.16)', borderRadius: 12, background: 'rgba(74,222,128,0.035)', padding: 12 }}>
                  <div style={{ fontFamily: C.mono, fontSize: 9, color: C.green, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 9 }}>Customer outcome</div>
                  <div style={{ fontSize: 12, color: C.text, fontWeight: 800, marginBottom: 6 }}>Clear deliverable before commitment</div>
                  <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.55 }}>You can inspect the package structure, output type, delivery contents, and review scope free before any commercial discussion.</div>
                </div>
              </div>

              {/* Trust indicators */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 22, flexWrap: 'wrap' }}>
                {[
                  { icon: <Clock size={11}/>, text: `${service.deliveryHours}h delivery` },
                  { icon: <Shield size={11}/>, text: 'P.Eng reviewed' },
                  { icon: <CheckCircle2 size={11}/>, text: '1 revision included' },
                  { icon: <Package size={11}/>, text: `${deliverables.length} deliverables` },
                ].map(item => (
                  <div key={item.text} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: C.muted, padding: '5px 10px', background: 'rgba(240,246,255,0.03)', border: '1px solid rgba(240,246,255,0.07)', borderRadius: 20 }}>
                    <span style={{ color: C.cyan }}>{item.icon}</span>{item.text}
                  </div>
                ))}
              </div>

              {/* Acknowledgement CTA */}
              <div style={{ background: 'rgba(255,107,53,0.04)', border: '1px solid rgba(255,107,53,0.15)', borderRadius: 12, padding: '14px 18px', marginBottom: 18 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>Ready to view the free package scope?</div>
                <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.6 }}>
                  You now understand exactly what your <strong style={{ color: C.text }}>{service.name}</strong> package includes.
                  {founderMode ? ' Continue with Founder Access to prepare this workflow.' : ' Continue to see the issue-for-bid scope detail with no payment prompt.'}
                </div>
              </div>

              <button data-testid="reveal-pricing-btn"
                onClick={handleRevealPrice}
                style={{ padding: '12px 28px', borderRadius: 10, background: C.orange, border: 'none', color: C.bg, fontWeight: 800, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7 }}>
                {founderMode ? 'Activate Founder Workflow' : 'View Free Package Scope'} <ArrowRight size={15}/>
              </button>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              STEP 3 — FREE PACKAGE DETAIL
          ══════════════════════════════════════════════════════════════════ */}
          {step === 3 && (
            <div data-testid="discovery-step-3">
              <div style={{
                textAlign: 'center', marginBottom: 24,
                padding: '20px 16px',
                background: 'linear-gradient(135deg, rgba(0,212,255,0.03) 0%, rgba(0,0,0,0) 100%)',
                border: '1px solid rgba(0,212,255,0.12)',
                borderRadius: 16,
              }}>
                <div data-testid="free-package-detail-banner">
                  <div style={{ fontFamily: C.mono, fontSize: 10, color: 'rgba(0,212,255,0.55)', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 12 }}>{founderMode ? 'Founder Mode Active' : 'Free Access Preview'}</div>
                  <div style={{ fontSize: 34, fontWeight: 900, color: C.cyan, lineHeight: 1.05, textShadow: `0 0 60px rgba(0,212,255,0.35)` }}>Issue-for-Bid Detail Pack</div>
                  <div style={{ fontFamily: C.mono, fontSize: 10, color: 'rgba(240,246,255,0.42)', marginTop: 10 }}>No payment required to view this package scope. Commercial terms can be discussed after the customer understands the deliverable.</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
                  {[
                    { icon: <Shield size={10}/>, label: 'P.Eng Reviewed' },
                    { icon: <CheckCircle2 size={10}/>, label: 'ISO Compliant' },
                    { icon: <Star size={10}/>, label: '100% Satisfaction' },
                  ].map(item => (
                    <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'rgba(0,212,255,0.5)', fontFamily: C.mono }}>
                      <span style={{ color: C.cyan }}>{item.icon}</span>{item.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Scope summary */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 18 }}>
                {scopeItems.map(item => (
                  <div key={item.label} style={{ background: 'rgba(240,246,255,0.02)', border: '1px solid rgba(240,246,255,0.08)', borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ fontFamily: C.mono, fontSize: 9.5, color: 'rgba(240,246,255,0.35)', textTransform: 'uppercase', marginBottom: 3 }}>{item.label}</div>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: C.text }}>{item.value}</div>
                  </div>
                ))}
              </div>

              {/* Deliverables recap */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontFamily: C.mono, fontSize: 9, color: 'rgba(0,212,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>What you receive</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(SERVICE_DELIVERABLES[service.id] ?? SERVICE_DELIVERABLES['_default'] ?? []).map(d => (
                    <span key={d} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: C.muted, padding: '4px 10px', background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.18)', borderRadius: 20 }}>
                      <CheckCircle2 size={9} style={{ color: C.green }}/>{d}
                    </span>
                  ))}
                </div>
              </div>

              <div data-testid="issued-for-bid-detail-list" style={{ marginBottom: 18, display: 'grid', gap: 8 }}>
                <div style={{ fontFamily: C.mono, fontSize: 9, color: 'rgba(0,212,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Bid-level package detail</div>
                {BID_DETAIL_ITEMS.map((item) => (
                  <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: 'rgba(0,212,255,0.025)', border: '1px solid rgba(0,212,255,0.11)', borderRadius: 9, padding: '9px 11px', color: C.muted, fontSize: 11.5, lineHeight: 1.5 }}>
                    <CheckCircle2 size={12} style={{ color: C.cyan, flexShrink: 0, marginTop: 2 }}/>{item}
                  </div>
                ))}
              </div>

              <div style={{ background: 'rgba(0,212,255,0.03)', border: '1px solid rgba(0,212,255,0.1)', borderRadius: 10, padding: '10px 14px', marginBottom: 20, fontSize: 11.5, color: C.muted }}>
                <span style={{ color: C.cyan, fontWeight: 700 }}>Free access path:</span> request beta/founder access, upload sample project information, and receive a detailed R2 scope preview. Quotes and paid packages remain separate from this preview flow.
              </div>

              {/* CTAs */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button data-testid="proceed-checkout-btn"
                  onClick={handleCheckout}
                  style={{ flex: 2, padding: '13px 0', borderRadius: 10, background: C.orange, border: 'none', color: C.bg, fontWeight: 800, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, boxShadow: `0 0 30px rgba(255,107,53,0.2)` }}>
                  {ctaLabel} <ChevronRight size={15}/>
                </button>
                {!isReturning && (
                  <button onClick={() => setStep(1)}
                    data-testid="revise-scope-btn"
                    style={{ flex: 1, padding: '13px 0', borderRadius: 10, background: 'transparent', border: '1px solid rgba(240,246,255,0.1)', color: 'rgba(240,246,255,0.4)', fontSize: 12, cursor: 'pointer' }}>
                    ← Revise Scope
                  </button>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
      <style>{`@media (max-width: 720px){.r2-discovery-output-grid{grid-template-columns:1fr!important}}`}</style>
    </div>
  );
}
